import type { RefundLineInput, SlipSnapshot, TenderInput } from '@didto/domain';
import {
  cashSuggestions,
  changeForTender,
  formatMinor,
  fractionDigitsFor,
  isCapturableMethodKind,
  minorToInput,
  parseMoneyToMinor,
  refundableMinor,
  remainingMinor,
  reversibleByPayment,
  slipReversalView,
  uuidv7,
  validateRefund,
  validateTenders,
  WRITE_OFF_LABEL,
} from '@didto/domain';
import {
  Badge,
  Button,
  Field,
  IconButton,
  Input,
  Select,
  Sheet,
  Textarea,
  Toggle,
} from '@didto/ui';
import { useState } from 'react';
import { formatInTimeZone } from '@didto/domain';
import { useOnline } from '@/lib/network';
import type { Order, OrderItem } from '@/lib/orders';
import type { OrderMoney, Payment, PaymentMethod, Receipt } from '@/lib/payments';
import { voidedPaymentIds } from '@/lib/payments';
import { LEDGER_ROW_TONE } from '@/lib/status';
import './pos.css';

/* ── Checkout: one or more tenders applied atomically ───────────────────────────────────── */

interface TenderDraft {
  key: string;
  payment_method_id: string;
  amount: string;
  tendered: string;
  reference: string;
}

export function CheckoutSheet({
  order,
  methods,
  currency,
  busy,
  onClose,
  onCapture,
}: {
  order: Order | null;
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onCapture: (tenders: TenderInput[], partial: boolean) => Promise<void>;
}) {
  return order ? (
    <CheckoutForm
      key={order.id + ':' + order.version}
      order={order}
      methods={methods}
      currency={currency}
      busy={busy}
      onClose={onClose}
      onCapture={onCapture}
    />
  ) : null;
}

function CheckoutForm({
  order,
  methods,
  currency,
  busy,
  onClose,
  onCapture,
}: {
  order: Order;
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onCapture: (tenders: TenderInput[], partial: boolean) => Promise<void>;
}) {
  const fd = fractionDigitsFor(currency);
  const remaining = remainingMinor({
    totalMinor: order.total_minor,
    paidMinor: order.paid_minor,
    refundedMinor: order.refunded_minor,
    writtenOffMinor: order.written_off_minor,
  });
  // ADR-031 (D4): complimentary is not a way of paying; an old method of that kind is never offered
  const enabled = methods.filter(
    (m) => m.enabled && !m.archived_at && isCapturableMethodKind(m.kind),
  );
  const cash = enabled.find((m) => m.kind === 'cash') ?? enabled[0];
  const [tenders, setTenders] = useState<TenderDraft[]>(() => [
    {
      key: uuidv7(),
      payment_method_id: cash?.id ?? '',
      amount: minorToInput(remaining, fd),
      tendered: '',
      reference: '',
    },
  ]);
  const [partial, setPartial] = useState(false);
  const online = useOnline();

  const methodOf = (id: string) => enabled.find((m) => m.id === id);
  const parse = (text: string) =>
    text.trim() ? parseMoneyToMinor(text, { fractionDigits: fd }) : null;
  const inputs: TenderInput[] = tenders.map((t) => {
    const m = methodOf(t.payment_method_id);
    const amount = parse(t.amount) ?? 0;
    const tendered = m?.kind === 'cash' ? parse(t.tendered) : null;
    return {
      id: t.key,
      payment_method_id: t.payment_method_id,
      amount_minor: amount,
      tendered_minor: tendered != null && tendered > 0 ? tendered : null,
      reference: t.reference.trim() || null,
    };
  });
  const sum = inputs.reduce((a, t) => a + (t.amount_minor > 0 ? t.amount_minor : 0), 0);
  const change = inputs.reduce(
    (a, t) => a + changeForTender(t, methodOf(t.payment_method_id)?.kind ?? 'other'),
    0,
  );
  const short = Math.max(0, remaining - sum);
  const error = validateTenders(inputs, {
    remainingMinor: remaining,
    methods: enabled.map((m) => ({
      id: m.id,
      kind: m.kind,
      enabled: m.enabled,
      requires_reference: m.requires_reference,
    })),
    partialAllowed: partial,
  });

  const update = (key: string, patch: Partial<TenderDraft>) =>
    setTenders((cur) => cur.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  const remove = (key: string) => setTenders((cur) => cur.filter((t) => t.key !== key));
  const add = () => {
    const others = sum;
    const rest = Math.max(0, remaining - others);
    const next =
      enabled.find((m) => !tenders.some((t) => t.payment_method_id === m.id)) ?? enabled[0];
    setTenders((cur) => [
      ...cur,
      {
        key: uuidv7(),
        payment_method_id: next?.id ?? '',
        amount: minorToInput(rest, fd),
        tendered: '',
        reference: '',
      },
    ]);
  };

  return (
    <Sheet open onClose={onClose} title={`Pay ${order.order_number}`}>
      <div className="d-stack" data-testid="checkout-sheet">
        <div className="pos-due">
          <span className="pos-due__label">Remaining</span>
          <strong className="pos-due__value" data-testid="checkout-remaining">
            {formatMinor(remaining, currency)}
          </strong>
        </div>
        {tenders.map((t, i) => {
          const m = methodOf(t.payment_method_id);
          const amountMinor = parse(t.amount) ?? 0;
          const tenderedMinor = parse(t.tendered);
          return (
            <div
              className="d-surface d-surface--flat d-surface--pad-sm pos-tender"
              key={t.key}
              data-testid="tender"
            >
              <div className="pos-tender__head">
                <div className="d-pills" role="group" aria-label={`Payment method ${i + 1}`}>
                  {enabled.map((pm) => (
                    <button
                      key={pm.id}
                      type="button"
                      className="d-pill"
                      aria-pressed={pm.id === t.payment_method_id}
                      onClick={() =>
                        update(t.key, { payment_method_id: pm.id, tendered: '', reference: '' })
                      }
                    >
                      {pm.name}
                    </button>
                  ))}
                </div>
                {tenders.length > 1 && (
                  <IconButton
                    variant="quiet"
                    size="sm"
                    label={`remove payment ${i + 1}`}
                    onClick={() => remove(t.key)}
                  >
                    ✕
                  </IconButton>
                )}
              </div>
              <div className="pos-fields-2">
                <Field label="Amount">
                  {(id) => (
                    <Input
                      id={id}
                      className="d-input--money"
                      inputMode="decimal"
                      value={t.amount}
                      onChange={(e) => update(t.key, { amount: e.target.value })}
                      data-testid="tender-amount"
                    />
                  )}
                </Field>
                {m?.kind === 'cash' && (
                  <Field label="Cash tendered">
                    {(id) => (
                      <Input
                        id={id}
                        className="d-input--money"
                        inputMode="decimal"
                        placeholder={t.amount}
                        value={t.tendered}
                        onChange={(e) => update(t.key, { tendered: e.target.value })}
                        data-testid="tender-tendered"
                      />
                    )}
                  </Field>
                )}
              </div>
              {m?.kind === 'cash' && amountMinor > 0 && (
                <div className="pos-quick">
                  {cashSuggestions(amountMinor).map((v) => (
                    <Button
                      key={v}
                      size="sm"
                      variant="ghost"
                      onClick={() => update(t.key, { tendered: minorToInput(v, fd) })}
                    >
                      {formatMinor(v, currency)}
                    </Button>
                  ))}
                  {tenderedMinor != null && tenderedMinor >= amountMinor && (
                    <Badge tone="success">
                      Change {formatMinor(tenderedMinor - amountMinor, currency)}
                    </Badge>
                  )}
                </div>
              )}
              {m?.requires_reference && (
                <Field label={`${m.name} reference number`}>
                  {(id) => (
                    <Input
                      id={id}
                      value={t.reference}
                      maxLength={64}
                      placeholder="as shown on the customer's confirmation"
                      onChange={(e) => update(t.key, { reference: e.target.value })}
                      data-testid="tender-reference"
                    />
                  )}
                </Field>
              )}
            </div>
          );
        })}
        <div className="d-spread">
          <Button
            variant="ghost"
            size="sm"
            onClick={add}
            disabled={tenders.length >= 5}
            data-testid="tender-add"
          >
            Add another payment
          </Button>
          {short > 0 && (
            <Toggle
              checked={partial}
              onChange={setPartial}
              label={`Short by ${formatMinor(short, currency)}: record as a deposit`}
            />
          )}
        </div>
        {short > 0 && partial && order.type !== 'advance' && (
          <p className="d-help">
            Regular orders take deposits only when the setting “allow partial payments” is on.
          </p>
        )}
        <div className="pos-sheet-foot">
          <div className="pos-sheet-foot__sum">
            <span className="pos-sheet-foot__amount">
              {change > 0
                ? `Change ${formatMinor(change, currency)}`
                : `Paying ${formatMinor(sum, currency)}`}
            </span>
            {error && (
              <p className="pos-problem" role="alert" data-testid="checkout-error">
                {error}
              </p>
            )}
          </div>
          <div className="pos-sheet-foot__actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              busy={busy}
              disabled={!online || !!error}
              onClick={() => void onCapture(inputs, partial && short > 0)}
              data-testid="checkout-confirm"
            >
              {partial && short > 0 ? 'Record deposit' : 'Take payment'}
            </Button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/* ── Refund ─────────────────────────────────────────────────────────────────────────────── */

export interface RefundInput {
  amount_minor: number;
  reason: string;
  payment_method_id?: string;
  lines?: RefundLineInput[];
}

export function RefundSheet({
  order,
  items,
  methods,
  currency,
  busy,
  onClose,
  onRefund,
}: {
  order: Order | null;
  items: OrderItem[];
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onRefund: (input: RefundInput) => Promise<void>;
}) {
  return order ? (
    <RefundForm
      key={order.id + ':' + order.version}
      order={order}
      items={items}
      methods={methods}
      currency={currency}
      busy={busy}
      onClose={onClose}
      onRefund={onRefund}
    />
  ) : null;
}

function RefundForm({
  order,
  items,
  methods,
  currency,
  busy,
  onClose,
  onRefund,
}: {
  order: Order;
  items: OrderItem[];
  methods: PaymentMethod[];
  currency: string;
  busy: boolean;
  onClose: () => void;
  onRefund: (input: RefundInput) => Promise<void>;
}) {
  const fd = fractionDigitsFor(currency);
  const refundable = refundableMinor({
    paidMinor: order.paid_minor,
    refundedMinor: order.refunded_minor,
  });
  const [amount, setAmount] = useState(minorToInput(refundable, fd));
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('');
  const [byLines, setByLines] = useState(false);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const active = items.filter((i) => i.status === 'active');
  const lines: RefundLineInput[] = active
    .filter((i) => (qtys[i.id] ?? 0) > 0)
    .map((i) => ({
      order_item_id: i.id,
      qty: qtys[i.id] ?? 0,
      amount_minor: Math.round((i.line_total_minor * (qtys[i.id] ?? 0)) / i.qty),
    }));
  const lineSum = lines.reduce((a, l) => a + l.amount_minor, 0);
  const amountMinor = byLines ? lineSum : (parseMoneyToMinor(amount, { fractionDigits: fd }) ?? 0);
  const error = validateRefund(
    { amount_minor: amountMinor, reason, lines: byLines ? lines : undefined },
    { refundableMinor: refundable },
  );
  const enabled = methods.filter((m) => m.enabled && !m.archived_at);
  const online = useOnline();
  const backBy = enabled.find((m) => m.id === method)?.name ?? null;
  return (
    <Sheet open onClose={onClose} title={`Refund ${order.order_number}`}>
      <div className="d-stack" data-testid="refund-sheet">
        <div className="pos-due">
          <span className="pos-due__label">Refundable</span>
          <strong className="pos-due__value">{formatMinor(refundable, currency)}</strong>
        </div>
        <Toggle checked={byLines} onChange={setByLines} label="Refund whole lines" />
        {byLines ? (
          <div className="pos-lines">
            {active.map((i) => (
              <div className="pos-line" key={i.id}>
                <div className="pos-line__main">
                  <div className="pos-line__title">
                    <span className="pos-line__qty">{i.qty} ×</span> {i.name_snapshot}
                  </div>
                  <div className="pos-line__detail d-mono">
                    {formatMinor(i.line_total_minor, currency)}
                  </div>
                </div>
                <span className="d-qty">
                  <IconButton
                    size="sm"
                    label={`fewer ${i.name_snapshot}`}
                    disabled={(qtys[i.id] ?? 0) <= 0}
                    onClick={() => setQtys({ ...qtys, [i.id]: (qtys[i.id] ?? 0) - 1 })}
                  >
                    −
                  </IconButton>
                  <span className="d-qty__value">{qtys[i.id] ?? 0}</span>
                  <IconButton
                    size="sm"
                    label={`more ${i.name_snapshot}`}
                    disabled={(qtys[i.id] ?? 0) >= i.qty}
                    onClick={() => setQtys({ ...qtys, [i.id]: (qtys[i.id] ?? 0) + 1 })}
                  >
                    +
                  </IconButton>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Field label="Amount">
            {(id) => (
              <Input
                id={id}
                className="d-input--money"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="refund-amount"
              />
            )}
          </Field>
        )}
        <Field label="Refund by" help="Defaults to the method the order was paid with.">
          {(id) => (
            <Select
              id={id}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="refund-method"
            >
              <option value="">Same as the payment</option>
              {enabled.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Reason" help="Required; it is kept in the audit log.">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="refund-reason"
            />
          )}
        </Field>
        <div className="pos-sheet-foot">
          <div className="pos-sheet-foot__sum">
            <strong className="pos-sheet-foot__amount">{formatMinor(amountMinor, currency)}</strong>
            <span className="d-muted">
              {backBy ? `Goes back by ${backBy}.` : 'Goes back the way it was paid.'}
            </span>
            {error && (
              <p className="pos-problem" role="alert">
                {error}
              </p>
            )}
          </div>
          <div className="pos-sheet-foot__actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger-solid"
              busy={busy}
              disabled={!online || !!error}
              onClick={() =>
                void onRefund({
                  amount_minor: amountMinor,
                  reason: reason.trim(),
                  ...(method ? { payment_method_id: method } : {}),
                  ...(byLines ? { lines } : {}),
                })
              }
              data-testid="refund-confirm"
            >
              Refund
            </Button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/* ── Money on an order: tenders, reversals, slips ───────────────────────────────────────── */

export function MoneyPanel({
  money,
  currency,
  timeZone,
  canVoid,
  onVoid,
  onSlip,
}: {
  money: OrderMoney | undefined;
  currency: string;
  timeZone: string;
  canVoid: boolean;
  /** Called with the tender and what a void would reverse: its amount minus what was refunded. */
  onVoid?: (p: Payment, reversibleMinor: number) => void;
  onSlip: (r: Receipt) => void;
}) {
  const online = useOnline();
  if (!money || (money.payments.length === 0 && money.reversals.length === 0)) return null;
  const voided = voidedPaymentIds(money.reversals);
  const left = reversibleByPayment(money.payments, money.reversals);
  const refundedFrom = (id: string) =>
    money.reversals
      .filter((r) => r.kind === 'refund' && r.payment_id === id && !r.cancelled)
      .reduce((s, r) => s + r.amount_minor, 0);
  return (
    <div className="pos-money-panel" data-testid="money-panel">
      <div className="d-eyebrow">Payments</div>
      <div className="pos-lines">
        {money.payments.map((p) => (
          <div
            className={`pos-line${voided.has(p.id) ? ' pos-line--void' : ''}`}
            key={p.id}
            data-testid="payment-row"
          >
            <div className="pos-line__main">
              <div className="pos-line__title">{p.method_name_snapshot}</div>
              <div className="pos-line__detail">
                {p.tendered_minor != null && p.tendered_minor !== p.amount_minor
                  ? `tendered ${formatMinor(p.tendered_minor, currency)}, change ${formatMinor(p.change_minor, currency)} · `
                  : ''}
                {p.reference ? `ref ${p.reference} · ` : ''}
                {refundedFrom(p.id) > 0
                  ? `${formatMinor(refundedFrom(p.id), currency)} refunded · `
                  : ''}
                {p.owner_entry ? 'recorded by the owner after the close · ' : ''}
                {formatInTimeZone(p.captured_at, timeZone)}
              </div>
            </div>
            <div className="pos-line__side">
              <span className="pos-line__total">{formatMinor(p.amount_minor, currency)}</span>
              {voided.has(p.id) ? (
                <Badge tone={LEDGER_ROW_TONE.voided}>voided</Badge>
              ) : p.status === 'captured' && (left.get(p.id) ?? 0) <= 0 ? (
                <Badge tone={LEDGER_ROW_TONE.refunded}>refunded</Badge>
              ) : (
                canVoid &&
                onVoid && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!online}
                    onClick={() => onVoid(p, left.get(p.id) ?? 0)}
                    aria-label={`void payment ${p.method_name_snapshot}`}
                  >
                    Void
                  </Button>
                )
              )}
            </div>
          </div>
        ))}
        {money.reversals
          .filter((r) => r.kind === 'refund')
          .map((r) => (
            <div className="pos-line" key={r.id} data-testid="refund-row">
              <div className="pos-line__main">
                <div className="pos-line__title">Refund · {r.method_name_snapshot}</div>
                <div className="pos-line__detail">
                  {r.reason}
                  {r.method_override ? ' · different method' : ''}
                  {r.owner_entry ? ' · recorded by the owner after the close' : ''} ·{' '}
                  {formatInTimeZone(r.occurred_at, timeZone)}
                </div>
              </div>
              <div className="pos-line__side">
                <span className="pos-line__total">{formatMinor(r.amount_minor, currency)}</span>
                {r.cancelled ? (
                  <Badge tone={LEDGER_ROW_TONE.cancelled}>cancelled</Badge>
                ) : (
                  <Badge tone={LEDGER_ROW_TONE.refunded}>refunded</Badge>
                )}
              </div>
            </div>
          ))}
      </div>
      {money.receipts.length > 0 && (
        <div className="pos-slips" data-testid="slips">
          {money.receipts.map((r) => (
            <Button key={r.id} size="sm" variant="ghost" onClick={() => onSlip(r)}>
              Slip {r.display_number} · {r.kind}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Internal payment slip (owner decision 2: internal verification only) ───────────────── */

export function SlipSheet({
  receipt,
  currency,
  onClose,
}: {
  receipt: Receipt | null;
  currency: string;
  onClose: () => void;
}) {
  const s: SlipSnapshot | null = receipt?.snapshot ?? null;
  // what this slip reversed (each tender), then the rest of the order's reversals
  const view = s ? slipReversalView(s) : null;
  return (
    <Sheet
      open={!!receipt}
      onClose={onClose}
      title={receipt ? `Slip ${receipt.display_number}` : 'Slip'}
    >
      {s && (
        <div className="d-slip" data-testid="slip">
          <div className="d-slip__head">
            <div>{s.label}</div>
            <div className="d-faint">For internal verification only</div>
          </div>
          <div className="d-slip__row">
            <span>{s.business_name}</span>
            <span>{s.location_name}</span>
          </div>
          <div className="d-slip__row">
            <span>
              Order {s.order_number} · {s.order_type.replace('_', ' ')}
              {s.table_name ? ` · ${s.table_name}` : ''}
              {s.customer_name ? ` · ${s.customer_name}` : ''}
            </span>
            <span>{s.display_number}</span>
          </div>
          <div className="d-slip__rule" />
          {s.lines.map((l, i) => (
            <div className={`d-slip__row${l.status === 'voided' ? ' pos-slip-void' : ''}`} key={i}>
              <span>
                {l.qty} × {l.name}
                {l.detail ? ` (${l.detail})` : ''}
              </span>
              <span>{formatMinor(l.line_total_minor, currency)}</span>
            </div>
          ))}
          <div className="d-slip__rule" />
          {/* ADR-032: served items handed back come off the total; nothing goes back to stock */}
          {(s.returns ?? []).length > 0 && (
            <>
              <div className="d-slip__row">
                <span>Subtotal</span>
                <span>{formatMinor(s.subtotal_minor, currency)}</span>
              </div>
              {(s.returns ?? []).map((r) => (
                <div className="d-slip__row" key={r.id} data-testid="slip-return">
                  <span>
                    Returned · {r.qty} × {r.line}
                  </span>
                  <span>−{formatMinor(r.amount_minor, currency)}</span>
                </div>
              ))}
            </>
          )}
          <div className="d-slip__row">
            <strong>Total</strong>
            <strong>{formatMinor(s.total_minor, currency)}</strong>
          </div>
          {s.tenders.map((t, i) => (
            <div className="d-slip__row" key={i}>
              <span>
                {t.method}
                {t.reference ? ` ref ${t.reference}` : ''}
                {t.tendered_minor != null && t.tendered_minor !== t.amount_minor
                  ? ` (tendered ${formatMinor(t.tendered_minor, currency)})`
                  : ''}
              </span>
              <span>{formatMinor(t.amount_minor, currency)}</span>
            </div>
          ))}
          {s.change_minor > 0 && (
            <div className="d-slip__row">
              <span>Change</span>
              <span>{formatMinor(s.change_minor, currency)}</span>
            </div>
          )}
          {view?.own && (
            <div data-testid="slip-own-reversal">
              <div className="d-slip__row">
                <strong>
                  {view.own.kind === 'void' ? 'Voided' : 'Refunded'} · {view.own.reason}
                </strong>
                <strong>−{formatMinor(view.own.amount_minor, currency)}</strong>
              </div>
              {view.own.parts.map((p) => (
                <div className="d-slip__row" key={p.reversal_id} data-testid="slip-part">
                  <span>
                    {p.tender_method ?? p.method}
                    {p.method_override ? ` (given back by ${p.method})` : ''}
                  </span>
                  <span>−{formatMinor(p.amount_minor, currency)}</span>
                </div>
              ))}
            </div>
          )}
          {view?.history.map((r, i) => (
            <div className="d-slip__row" key={r.id ?? i}>
              <span>
                {r.kind === 'void' ? 'Voided' : 'Refunded'} · {r.method} · {r.reason}
              </span>
              <span>−{formatMinor(r.amount_minor, currency)}</span>
            </div>
          ))}
          {s.net_minor != null && s.reversals.length > 0 && (
            <div className="d-slip__row">
              <span>Net paid</span>
              <span>{formatMinor(s.net_minor, currency)}</span>
            </div>
          )}
          {/* ADR-031: written off, not paid; with the net paid it makes the total */}
          {(s.write_offs ?? []).map((w) => (
            <div className="d-slip__row" key={w.id} data-testid="slip-write-off">
              <span>
                {w.kind === 'reinstated'
                  ? 'Reinstated'
                  : `Written off · ${WRITE_OFF_LABEL[w.kind]}`}
              </span>
              <span>
                {w.kind === 'reinstated' ? '+' : '−'}
                {formatMinor(w.amount_minor, currency)}
              </span>
            </div>
          ))}
          <div className="d-slip__rule" />
          <div className="d-slip__row d-faint">
            <span>
              {s.terminal_code} · {s.issued_by ?? ''}
            </span>
            <span>{new Date(s.issued_at).toLocaleString('en-PH')}</span>
          </div>
        </div>
      )}
    </Sheet>
  );
}
